import { PaymentMethodsResponse } from '@adyen/adyen-web/dist/types/core/ProcessResponse/PaymentMethodsResponse/types';
import { useCallback, useEffect, useState } from 'react';

import RetryNotification from '../RetryNotification/RetryNotification';
import RetryPaymentLoader from '../RetryPaymentLoader/RetryPaymentLoader';

import GooglePayButton from '@features/GooglePay/GooglePayButton/GooglePayButton';
import { useTranslation } from '@hooks/useTranslations';
import { useAppContext } from '@providers/AppContextProvider';
import { useConfig } from '@providers/ConfigProvider';
import { useRetryState } from '@providers/RetryDataProvider';
import { useRetryPaymentContext } from '@providers/RetryPaymentContextProvider';
import { useThunkDispatch } from '@providers/ThunkDispatchProvider';
import { getLocalPaymentMethods, initiateGooglePayment } from '@services/retryPaymentService';
import { captureException } from '@services/sentryService';
import { getOrderDetailsUrl } from '@utils/retryUtils';
import { convertMajorUnitsToMinorUnits, convertRunningEnvToAdyenEnv } from '@utils/utils';

import styles from './RetryPaymentGooglePay.module.scss';

export type RetryPaymentGooglePayProps = {
  orderId: string;
  siteId: number;
  currency: string;
  customerId: string;
  amount: number;
  sid?: string;
};

const RetryPaymentGooglePay: React.FC<RetryPaymentGooglePayProps> = ({
  orderId,
  siteId,
  currency,
  customerId,
  amount,
  sid,
}) => {
  const { csrfToken } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [countryCode, setCountryCode] = useState('');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsResponse>({
    paymentMethods: [],
  });
  const { setRetryPaymentInfo } = useRetryPaymentContext();
  const {
    retryState: { initiatePaymentData, processAuthorisationData },
    processPaymentAuthorisationSuccess,
  } = useRetryState();
  const { thunkDispatch } = useThunkDispatch();
  const {
    config: { env },
  } = useConfig();
  const { t } = useTranslation();
  const { isFetching } = processAuthorisationData ?? {};
  const isLoading = loading || isFetching;

  useEffect(() => {
    // Request to fetch paymentMethodsResponse & countryCode
    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        const response = await getLocalPaymentMethods(siteId, customerId, amount);

        setCountryCode(response.countryCode);
        // Adapt the JSON response from retry-payment to the object that is accepted by Adyen
        const paymentMethodsResponse: PaymentMethodsResponse = {
          paymentMethods: response.paymentMethods,
        };
        setPaymentMethods(paymentMethodsResponse);
      } catch (err: unknown) {
        console.info('Error while getting local payment methods:', err);
        captureException(err as Error);
        setHasError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    if (initiatePaymentData) {
      if (initiatePaymentData?.isFetching) {
        return;
      }
      if (!initiatePaymentData?.actionObject) {
        const { retryPaymentStatus: initiateRetryStatus } = initiatePaymentData || {};
        if (initiateRetryStatus) {
          processPaymentAuthorisationSuccess({ retryPaymentStatus: initiateRetryStatus });
        }
      }
    }
  }, [initiatePaymentData, initiatePaymentData?.isFetching]);

  const onSubmit = useCallback(
    async ({ googlePayToken, checkoutAttemptId, googlePayCardNetwork }) => {
      setLoading(true);
      try {
        const returnUrl = getOrderDetailsUrl();
        thunkDispatch(
          initiateGooglePayment({
            csrfToken: csrfToken as string,
            tokenId: googlePayToken,
            googlePaycheckoutAttemptId: checkoutAttemptId,
            orderId,
            customerId,
            googlePayCardNetwork,
            returnUrl,
          }),
        );
      } catch (e: unknown) {
        captureException(e as Error);
        setRetryPaymentInfo({ retryPossible: false, retryPaymentStatus: 'UNKNOWN' });
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const onFatalError = useCallback(
    (_error: unknown) => {
      setRetryPaymentInfo({ retryPossible: false, retryPaymentStatus: 'CANCELLED' });
    },
    [setRetryPaymentInfo],
  );

  const minorUnitsAmount = convertMajorUnitsToMinorUnits(amount);

  if (!loading && hasError) {
    return (
      <RetryNotification
        id="retryPaymentGooglePayError"
        sid={sid}
        currency={currency}
        siteId={siteId}
        customerId={customerId}
        ariaLabel={t('general.error.internal')}
        type="warning">
        {t('general.error.internal')}
      </RetryNotification>
    );
  }

  return (
    <div className={styles.retryGooglePayPayment}>
      {isLoading && <RetryPaymentLoader />}

      <RetryNotification
        id="retryPaymentNotification"
        sid={sid}
        currency={currency}
        siteId={siteId}
        customerId={customerId}
        ariaLabel={t('mzp.order.detail.retry.payment.accessibility.label')}
        type="warning">
        {!isLoading && (
          <div className={styles.retryGooglePayPaymentNotification}>
            {t('mzp.order.detail.payment.reminder.warning.googlepay')}
            <div
              className={styles.retryGooglePayPaymentButtonContainer}
              data-zta="retryGooglePayPaymentContainer">
              <GooglePayButton
                adyenEnvironment={convertRunningEnvToAdyenEnv(env)}
                countryCode={countryCode}
                currency={currency}
                amount={minorUnitsAmount}
                paymentMethodsResponse={paymentMethods}
                onSubmit={onSubmit}
                addPaymentFrontendError={onFatalError}
              />
            </div>
          </div>
        )}
      </RetryNotification>
    </div>
  );
};

export default RetryPaymentGooglePay;
